import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Award,
  Download,
  Copy,
  AlertTriangle,
  ArrowLeft,
  ShieldCheck,
  CheckCircle2,
  Clock3,
} from 'lucide-react';
import { apiGet } from '../../services/api';
import toast from 'react-hot-toast';

export default function CertificatesPage() {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    fetchCertificates();
  }, []);

  const fetchCertificates = async () => {
    try {
      const data = await apiGet('/lms/certificates');
      setCertificates(data.certificates || []);
    } catch {
      toast.error('Failed to load certificates');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (certId) => {
    setDownloading(certId);
    try {
      const data = await apiGet(`/lms/certificates/${certId}/download`);
      let url = data.downloadUrl;
      if (url) {
        if (url.startsWith('/api/lms/certificates/')) {
          url = url.replace('/api/lms/certificates/', '/certificates/');
        }
        if (url.startsWith('/')) {
          url = `${window.location.protocol}//${window.location.hostname}${window.location.port ? `:${window.location.port}` : ''}${url}`;
        }
        window.open(url, '_blank');
      } else {
        toast.error('No download URL returned');
      }
    } catch (err) {
      toast.error(err?.message || 'Download unavailable');
    } finally {
      setDownloading(null);
    }
  };

  const handleCopyVerify = async (cert) => {
    const verifyUrl = cert.verificationUrl || `${window.location.origin}/verify/certificate/${cert.certificateNumber}`;
    try {
      await navigator.clipboard.writeText(verifyUrl);
      toast.success('Verification URL copied');
    } catch {
      toast.error('Could not copy verification URL');
    }
  };

  const isExpired = (cert) => cert.expiresAt && new Date(cert.expiresAt) < new Date();

  const isExpiringSoon = (cert) => {
    if (!cert.expiresAt || isExpired(cert)) return false;
    return new Date(cert.expiresAt) - new Date() < 90 * 24 * 60 * 60 * 1000;
  };

  const activeCertificates = certificates.filter((cert) => cert.status === 'active' || !cert.status);
  const revokedCertificates = certificates.filter((cert) => cert.status === 'revoked');
  const expiredCertificates = activeCertificates.filter(isExpired);
  const expiringSoonCertificates = activeCertificates.filter(isExpiringSoon);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link to="/training-management" className="p-2 rounded-lg hover:bg-gray-100 text-gray-500">
          <ArrowLeft size={20} />
        </Link>
        <div>
          <h1 className="text-2xl font-display font-bold text-gray-900 flex items-center gap-2">
            <Award className="text-amber-500" size={28} />
            Training Certificates
          </h1>
          <p className="text-gray-600 text-sm mt-0.5">
            View, verify, and download your issued course and learning path certificates.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <SummaryCard icon={CheckCircle2} label="Active" value={activeCertificates.length} tone="green" />
        <SummaryCard icon={Clock3} label="Expiring Soon" value={expiringSoonCertificates.length} tone="amber" />
        <SummaryCard icon={AlertTriangle} label="Expired" value={expiredCertificates.length} tone="red" />
        <SummaryCard icon={ShieldCheck} label="Revoked" value={revokedCertificates.length} tone="gray" />
      </div>

      {certificates.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Award size={48} className="mx-auto text-gray-300 mb-3" />
          <p className="font-semibold text-gray-900 mb-1">No certificates yet</p>
          <p className="text-sm text-gray-500 mb-4">
            Certificates appear here after a learner completes a certificate-enabled course.
          </p>
          <Link
            to="/training-management"
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700"
          >
            Go to Training Management
          </Link>
        </div>
      ) : (
        <>
          {activeCertificates.length > 0 && (
            <section className="space-y-3">
              <div>
                <h2 className="font-semibold text-gray-900">Active Certificates</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Download PDFs, verify certificate numbers, and monitor expiration.
                </p>
              </div>
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {activeCertificates.map((cert) => (
                  <CertificateCard
                    key={cert._id}
                    cert={cert}
                    expired={isExpired(cert)}
                    expiringSoon={isExpiringSoon(cert)}
                    downloading={downloading === cert._id}
                    onDownload={() => handleDownload(cert._id)}
                    onCopyVerify={() => handleCopyVerify(cert)}
                  />
                ))}
              </div>
            </section>
          )}

          {revokedCertificates.length > 0 && (
            <section className="space-y-3">
              <h2 className="font-semibold text-gray-500 text-sm uppercase tracking-wide">
                Revoked ({revokedCertificates.length})
              </h2>
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {revokedCertificates.map((cert) => (
                  <CertificateCard key={cert._id} cert={cert} revoked />
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, tone }) {
  const tones = {
    green: 'bg-green-50 text-green-600',
    amber: 'bg-amber-50 text-amber-600',
    red: 'bg-red-50 text-red-600',
    gray: 'bg-gray-100 text-gray-600',
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tones[tone]}`}>
        <Icon size={18} />
      </div>
      <div>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        <p className="text-xs text-gray-500 mt-0.5">{label}</p>
      </div>
    </div>
  );
}

function CertificateCard({ cert, expired, expiringSoon, revoked, downloading, onDownload, onCopyVerify }) {
  const courseTitle =
    cert.learningPathTitle ||
    cert.learningPathId?.title ||
    cert.courseTitle ||
    cert.courseId?.title ||
    'Unknown Achievement';
  const issuedDate = cert.issuedAt ? new Date(cert.issuedAt).toLocaleDateString() : '-';
  const expiryDate = cert.expiresAt ? new Date(cert.expiresAt).toLocaleDateString() : null;
  const achievementLabel = cert.achievementType === 'learning_path' ? 'Learning Path' : 'Course';

  return (
    <div
      className={`bg-white rounded-xl border p-5 space-y-3 ${
        revoked
          ? 'border-gray-200 opacity-60'
          : expired
          ? 'border-red-200'
          : expiringSoon
          ? 'border-amber-200'
          : 'border-gray-200'
      }`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
            revoked
              ? 'bg-gray-100'
              : expired
              ? 'bg-red-100'
              : expiringSoon
              ? 'bg-amber-100'
              : 'bg-amber-100'
          }`}
        >
          {revoked ? (
            <AlertTriangle size={20} className="text-gray-500" />
          ) : expired ? (
            <AlertTriangle size={20} className="text-red-500" />
          ) : (
            <Award size={20} className="text-amber-500" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-900 text-sm truncate">{courseTitle}</p>
          <p className="text-xs text-gray-400 mt-0.5">{achievementLabel}</p>
          {cert.recipientName && <p className="text-xs text-gray-400 mt-0.5">{cert.recipientName}</p>}
          {cert.certificateNumber && (
            <p className="text-xs text-gray-400 font-mono mt-0.5">{cert.certificateNumber}</p>
          )}
        </div>
        {revoked && (
          <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded-full font-medium shrink-0">
            Revoked
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs text-gray-500">
        <div>
          <p className="text-gray-400">Issued</p>
          <p className="font-medium text-gray-700">{issuedDate}</p>
        </div>
        {expiryDate && (
          <div>
            <p className="text-gray-400">Expires</p>
            <p className={`font-medium ${expired ? 'text-red-600' : expiringSoon ? 'text-amber-600' : 'text-gray-700'}`}>
              {expiryDate}
              {expired && ' (expired)'}
              {!expired && expiringSoon && ' (soon)'}
            </p>
          </div>
        )}
        {cert.score !== undefined && cert.score !== null && (
          <div>
            <p className="text-gray-400">Score</p>
            <p className="font-medium text-green-700">{cert.score}%</p>
          </div>
        )}
        {cert.ceCredits?.amount > 0 && (
          <div>
            <p className="text-gray-400">CE Credits</p>
            <p className="font-medium text-gray-700">
              {cert.ceCredits.amount} {cert.ceCredits.type || 'credits'}
            </p>
          </div>
        )}
        {cert.downloadCount != null && (
          <div>
            <p className="text-gray-400">Downloads</p>
            <p className="font-medium text-gray-700">{cert.downloadCount}</p>
          </div>
        )}
      </div>

      {!revoked && (
        <div className="flex gap-2 pt-1">
          <button
            onClick={onDownload}
            disabled={downloading}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-primary-600 text-white rounded-lg text-xs font-semibold hover:bg-primary-700 disabled:bg-gray-300"
          >
            <Download size={13} />
            {downloading ? 'Getting link...' : 'Download PDF'}
          </button>
          <button
            onClick={onCopyVerify}
            title="Copy verification URL"
            className="flex items-center justify-center gap-1.5 px-3 py-2 border border-gray-300 rounded-lg text-xs text-gray-600 hover:bg-gray-50"
          >
            <Copy size={13} />
            Verify
          </button>
        </div>
      )}
    </div>
  );
}
